import { UPDATE_CHAT_NAME, USER_SET_CID, USER_SET_FBCLID, USER_SET_REF, USER_SET_TTCLID } from '@/assets/config/api.config'
import axios from 'axios'
import { getAuth } from 'firebase/auth'
import { createStore } from 'vuex'
import i18n from '@/i18n'

const initialState = () => ({
  styles: [],
  models: [],
  currentChat: {
    title: 'New Chat',
    type: 'text'
  },
  user: null,
  chatList: null,
  userDoc: null,
  userDocListenerEnabled: false,
  mainFolders: [],
  allFolders: [],
  smallMenuLayout: false,
  showCreateFolder: false,
  showSearchPopup: false,
  showEditFolder: false,
  folderToBeEdited: null,
  showMoveFolderOrChat: false,
  folderToMove: null,
  chatToMove: null,
  showCannotMoveFolder: false,
  showCannotCreateFolder: false,
  showOfferPopup: false,
  showPaidTrialPopup: false,
  selectedModel: null,
  selectedStyle: null,
  selectedModelType: 'text',
  showSelectStyle: false,
  promptImproverLimitReached: false,
  emptyChatSystemPrompt: '',
  isShortcutMenuOpened: false,
  canvaActive: false,
  canvaDBDocument: null,
  showModelPopup: false,
  importedTextToCanva: '',
  isCanvaElaborating: false,
  isCanvaOptionSelected: false,
  showPremiumPopup: false,
  showNewChatForbidden: false,
  showModelWarningPopup: false,
  modelWarningContent: '',
  currentChatTotalTokens: 0,
  showModelSpeedInfoPopup: false,
  showSpeedRestorePopup: false,
  showThunderAttachmentWarning: false,
  isWebSearchActive: false,
  // DeepResearch state
  isDeepResearchActive: false,
  deepResearchLimitReached: false,
  isDeepResearchPending: false,
  pendingDeepResearchQuery: '',
  showAppleSubscriptionInfo: false,
  showGoogleSubscriptionInfo: false,
  purchaseSuccessful: false,
  purchaseError: false,
  reportMessageID: null,
  showProfileSettings: false,
  subscriptionFromAnotherPlatform: false,
  showProfileDeletePopup: false,
  showUpgradeSubscriptionPopup: false,
  showManageCancelledPlanPopup: false,
  showReactivatePlanPopup: false,
  showSubscriptionUpdatedPopup: false,
  subscriptionCancellationContext: null,
  showCancelSubscriptionPopup: false,
  showCancelSubscriptionOfferViolationPopup: false,
  showSubscriptionCancelledPopup: false,
  showToolLimitPopup: false,
  showLoginPopup: false,
  toolLimitType: null,
  toolLimitHoursUntilReset: null,
  chatModels: [],
  imageModels: [],
  audioModels: [],
  toolHistory: [],
  lastChatByType: {
    text: null,
    image: null,
    audio: null
  },
  messageTextareaFullPage: false,
  isSidebarCollapsed: false,
  isItMobile: window.innerWidth <= 768
});

export default createStore({
  state: initialState(),
  getters: {
    getStyles: state => state.styles,
    getModels: state => state.models,
    getUser: state => state.user,
    getCurrentChat: state => state.currentChat,
    getChatList: state => state.chatList,
    getUserDoc: state => state.userDoc,
    getUserDocListenerEnabled: state => state.userDocListenerEnabled,
    getSmallMenuLayout: state => state.smallMenuLayout,
    getMainFolders: state => state.mainFolders,
    getAllFolders: state => state.allFolders,
    showCreateFolder: state => state.showCreateFolder,
    showSearchPopup: state => state.showSearchPopup,
    showEditFolder: state => state.showEditFolder,
    folderToBeEdited: state => state.folderToBeEdited,
    showMoveFolderOrChat: state => state.showMoveFolderOrChat,
    folderToMove: state => state.folderToMove,
    chatToMove: state => state.chatToMove,
    showCannotMoveFolder: state => state.showCannotMoveFolder,
    showCannotCreateFolder: state => state.showCannotCreateFolder,
    showOfferPopup: state => state.showOfferPopup,
    showPaidTrialPopup: state => state.showPaidTrialPopup,
    getSelectedModel: state => state.selectedModel,
    getSelectedStyle: state => state.selectedStyle,
    getSelectedModelType: state => state.selectedModelType,
    showSelectStyle: state => state.showSelectStyle,
    getPromptImproverLimitReached: state => state.promptImproverLimitReached,
    getEmptyChatSystemPrompt: state => state.emptyChatSystemPrompt,
    isShortcutMenuOpened: state => state.isShortcutMenuOpened,
    isCanvaActive: state => state.canvaActive,
    getCanvaDBDocument: state => state.canvaDBDocument,
    getShowModelPopup: state => state.showModelPopup,
    getImportedTextToCanva: state => state.importedTextToCanva,
    isCanvaElaborating: state => state.isCanvaElaborating,
    isCanvaOptionSelected: state => state.isCanvaOptionSelected,
    getShowPremiumPopup: state => state.showPremiumPopup,
    getShowNewChatForbidden: state => state.showNewChatForbidden,
    getShowModelWarningPopup: state => state.showModelWarningPopup,
    getModelWarningContent: state => state.modelWarningContent,
    getCurrentChatTotalTokens: state => state.currentChatTotalTokens,
    getShowModelSpeedInfoPopup: state => state.showModelSpeedInfoPopup,
    getShowSpeedRestorePopup: state => state.showSpeedRestorePopup,
    getShowThunderAttachmentWarning: state => state.showThunderAttachmentWarning,
    getWebSearchActive: state => state.isWebSearchActive,
    // DeepResearch getters
    getDeepResearchActive: state => state.isDeepResearchActive,
    getDeepResearchLimitReached: state => state.deepResearchLimitReached,
    getIsDeepResearchPending: state => state.isDeepResearchPending,
    getPendingDeepResearchQuery: state => state.pendingDeepResearchQuery,
    getIsDeepResearchRunning: state => {
      // Use userDoc.activeDeepResearches instead of volatile state
      return state.userDoc && state.userDoc.activeDeepResearches > 0;
    },
    getShowAppleSubscriptionInfo: state => state.showAppleSubscriptionInfo,
    getShowGoogleSubscriptionInfo: state => state.showGoogleSubscriptionInfo,
    getPurchaseSuccessful: state => state.purchaseSuccessful,
    getPurchaseError: state => state.purchaseError,
    getReportMessageID: state => state.reportMessageID,
    getShowProfileSettings: state => state.showProfileSettings,
    getShowProfileDeletePopup: state => state.showProfileDeletePopup,
    getShowUpgradeSubscriptionPopup: state => state.showUpgradeSubscriptionPopup,
    getShowManageCancelledPlanPopup: state => state.showManageCancelledPlanPopup,
    getShowReactivatePlanPopup: state => state.showReactivatePlanPopup,
    getShowSubscriptionUpdatedPopup: state => state.showSubscriptionUpdatedPopup,
    getSubscriptionCancellationContext: state => state.subscriptionCancellationContext,
    getShowCancelSubscriptionPopup: state => state.showCancelSubscriptionPopup,
    getShowCancelSubscriptionOfferViolationPopup: state => state.showCancelSubscriptionOfferViolationPopup,
    getShowSubscriptionCancelledPopup: state => state.showSubscriptionCancelledPopup,
    getShowLoginPopup: state => state.showLoginPopup,
    getShowToolLimitPopup: state => state.showToolLimitPopup,
    getToolLimitType: state => state.toolLimitType,
    getToolLimitHoursUntilReset: state => state.toolLimitHoursUntilReset,
    isMobile: state => state.isItMobile,
    getChatModels: state => state.chatModels,
    getImageModels: state => state.imageModels,
    getAudioModels: state => state.audioModels,
    getToolHistory: state => state.toolHistory,
    getLastChatByType: state => state.lastChatByType,
    getMessageTextareaFullPage: state => state.messageTextareaFullPage,
    isSidebarCollapsed: state => state.isSidebarCollapsed
  },
  mutations: {
    setSidebarCollapsed(state, collapsed) {
      state.isSidebarCollapsed = collapsed
      if (!collapsed && state.canvaActive) {
        state.canvaActive = false;
      }
    },
    setStyles(state, styles) {
      state.styles = styles
    },
    setModels(state, models) {
      state.models = models
    },
    setSelectedModel(state, model) {
      if (!model.isDeprecated) {
        state.selectedModel = model;
      } else {
        if (model.type === 'text') {
          this.commit('setDefaultChatModel');
        } else {
          this.commit('setDefaultImageModel');
        }
      }
      // Se il modello selezionato è un modello immagine, disattiva automaticamente tutti i tools
      if (model && state.imageModels.includes(model.value)) {
        state.isWebSearchActive = false;
        state.isDeepResearchActive = false;
        state.isCanvaOptionSelected = false;
      }
    },
    setSelectedModelType(state, modelType) {
      if (state.selectedModelType === modelType) return;
      state.selectedModelType = modelType;
      if (modelType === 'text') {
        this.commit('setDefaultChatModel');
      } else if (modelType === 'image') {
        this.commit('setDefaultImageModel');
      } else if (modelType === 'audio') {
        this.commit('setDefaultAudioModel');
      }
    },
    setSelectedStyle(state, style) {
      state.selectedStyle = style
    },
    setUser(state, user) {
      state.user = user
    },
    setShowPremiumPopup(state, show) {
      state.showPremiumPopup = show
    },
    setShowModelSpeedInfoPopup(state, show) {
      state.showModelSpeedInfoPopup = show
    },
    setShowSpeedRestorePopup(state, show) {
      state.showSpeedRestorePopup = show
    },
    setCanvaActive(state, active) {
      state.canvaActive = active
    },
    setIsCanvaOptionSelected(state, selected) {
      state.isCanvaOptionSelected = selected
    },
    setCurrentChat(state, chat) {
      const safeChat = chat || {};
      const resolvedType = safeChat.type || 'text';
      state.currentChat = {
        ...safeChat,
        type: resolvedType
      };

      if (!state.lastChatByType) {
        state.lastChatByType = {
          text: null,
          image: null,
          audio: null
        };
      }

      if (Object.prototype.hasOwnProperty.call(state.lastChatByType, resolvedType)) {
        state.lastChatByType[resolvedType] = chat?.id || null;
      }
    },
    setChatList(state, chatList) {
      state.chatList = chatList

      if (!state.lastChatByType) {
        return;
      }

      const ensureChatExists = (chatId) => chatId && chatList?.some?.(chat => chat.id === chatId);

      Object.entries(state.lastChatByType).forEach(([type, chatId]) => {
        if (chatId && !ensureChatExists(chatId)) {
          state.lastChatByType[type] = null;
        }
      });
    },
    setShowCreateFolder(state, show) {
      state.showCreateFolder = show
    },
    setShowSearchPopup(state, show) {
      state.showSearchPopup = show
    },
    setShowEditFolder(state, show) {
      state.showEditFolder = show
    },
    setFolderToBeEdited(state, folder) {
      state.folderToBeEdited = folder
    },
    setShortcutMenuOpened(state, opened) {
      state.isShortcutMenuOpened = opened
    },
    setShowMoveFolderOrChat(state, show) {
      state.showMoveFolderOrChat = show
    },
    setFolderToMove(state, folder) {
      state.folderToMove = folder
    },
    setChatToMove(state, chat) {
      state.chatToMove = chat
    },
    setShowCannotMoveFolder(state, show) {
      state.showCannotMoveFolder = show
    },
    setShowCannotCreateFolder(state, show) {
      state.showCannotCreateFolder = show
    },
    setShowOfferPopup(state, show) {
      state.showOfferPopup = show
    },
    setShowPaidTrialPopup(state, show) {
      state.showPaidTrialPopup = show
    },
    setPromptImproverLimitReached(state, show) {
      state.promptImproverLimitReached = show
    },
    setEmptyChatSystemPrompt(state, prompt) {
      state.emptyChatSystemPrompt = prompt
    },
    async updateChatName(state, payload) {
      state.chatList.forEach(chat => {
        if (chat.id === payload.chatID) {
          chat.title = payload.title
        }
      })

      if (state.currentChat.id === payload.chatID) {
        state.currentChat.title = payload.title
      }

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        return
      }
      const token = await user.getIdToken()
      await axios.patch(UPDATE_CHAT_NAME, {
        chatID: payload.chatID,
        title: payload.title
      }, {
        headers: {
          Authorization: token
        }
      })
    },
    async setUserDoc(state, userDoc) {
      state.userDoc = userDoc
      // Sincronizza la lingua salvata nel DB
      if (userDoc.preferredLanguage) {
        i18n.global.locale.value = userDoc.preferredLanguage;
        localStorage.setItem('appLanguage', userDoc.preferredLanguage);
      }
      const c_id = localStorage.getItem('c_id');
      const ref = localStorage.getItem('ref');
      const ttclid = localStorage.getItem('ttclid');
      const fbclid = localStorage.getItem('fbclid');
      const auth = getAuth();
      const user = auth.currentUser;
      const token = await user.getIdToken()
      if (!userDoc.c_id && c_id) {
        const gclid = localStorage.getItem('gclid');
        await axios.post(USER_SET_CID, {
          campaignID: c_id,
          gclid: gclid
        }, {
          headers: {
            Authorization: token
          }
        });
      }

      if (!userDoc.ttclid && ttclid) {
        await axios.post(USER_SET_TTCLID, {
          ttclid: ttclid
        }, {
          headers: {
            Authorization: token
          }
        });
      }

      if (!userDoc.fbclid && fbclid) {
        await axios.post(USER_SET_FBCLID, {
          fbclid: fbclid
        }, {
          headers: {
            Authorization: token
          }
        });
      }

      if (!userDoc.ref && ref) {
        await axios.post(USER_SET_REF, {
          ref: ref
        }, {
          headers: {
            Authorization: token
          }
        });
      }
    },
    patchUserDoc(state, patch) {
      if (!state.userDoc || !patch) {
        return
      }

      state.userDoc = {
        ...state.userDoc,
        ...patch
      }
    },
    resetUserState(state) {
      state.user = null;
      state.userDoc = null;
      state.userDocListenerEnabled = false;
      state.selectedModel = null;
      state.selectedStyle = null;
      state.selectedModelType = 'text';
      state.currentChat = {
        title: 'New Chat',
        type: 'text'
      };
      state.lastChatByType = {
        text: null,
        image: null,
        audio: null
      };
    },
    setUserDocListenerEnabled(state, enabled) {
      state.userDocListenerEnabled = enabled
    },
    setMainFolders(state, folders) {
      state.mainFolders = folders
    },
    setAllFolders(state, folders) {
      state.allFolders = folders
    },
    setShowSelectStyle(state, show) {
      state.showSelectStyle = show
    },
    setShowModelPopup(state, show) {
      state.showModelPopup = show
    },
    setCanvaDBDocument(state, document) {
      state.canvaDBDocument = document
    },
    setImportedTextToCanva(state, text) {
      state.importedTextToCanva = text
    },
    setIsCanvaElaborating(state, elaborating) {
      state.isCanvaElaborating = elaborating
    },
    setShowNewChatForbidden(state, show) {
      state.showNewChatForbidden = show
    },
    setShowModelWarningPopup(state, show) {
      state.showModelWarningPopup = show
    },
    setModelWarningContent(state, content) {
      state.modelWarningContent = content
    },
    setCurrentChatTotalTokens(state, tokens) {
      state.currentChatTotalTokens = tokens
    },
    setShowThunderAttachmentWarning(state, show) {
      state.showThunderAttachmentWarning = show
    },
    setWebSearchActive(state, active) {
      state.isWebSearchActive = active
    },
    // DeepResearch mutations
    setDeepResearchActive(state, active) {
      state.isDeepResearchActive = active
    },
    setDeepResearchLimitReached(state, reached) {
      state.deepResearchLimitReached = reached
    },
    setIsDeepResearchPending(state, pending) {
      state.isDeepResearchPending = pending
    },
    setPendingDeepResearchQuery(state, query) {
      state.pendingDeepResearchQuery = query
    },
    setDefaultChatModel(state) {
      if (!state.chatModels || state.chatModels.length === 0) {
        return;
      }

      const isPremium = state.userDoc && state.userDoc.isPremium;

      if (isPremium) {
        const premiumModel = state.chatModels.find(model => model.value === 'openai/gpt-5.4-mini');
        if (premiumModel) {
          state.selectedModel = premiumModel;
        } else {
          state.selectedModel = state.chatModels[0]; // fallback al primo modello disponibile
        }
      } else {
        const freeModel = state.chatModels.find(model => model.value === 'openai/gpt-5.4-nano');
        if (freeModel) {
          state.selectedModel = freeModel;
        } else {
          state.selectedModel = state.chatModels[0]; // fallback al primo modello disponibile
        }
      }
    },
    setDefaultImageModel(state) {
      if (!state.imageModels || state.imageModels.length === 0) {
        return;
      }
      const isPremium = state.userDoc && state.userDoc.isPremium;
      if (isPremium) {
        // Se l'utente è premium, seleziona il primo modello immagine disponibile
        state.selectedModel = state.imageModels[0];
      } else {
        // Se l'utente non è premium, seleziona il primo modello immagine gratuito (se esiste)
        const freeImageModel = state.imageModels.find(model => model.isFree);
        if (freeImageModel) {
          state.selectedModel = freeImageModel;
        } else {
          state.selectedModel = state.imageModels[0]; // fallback al primo modello immagine disponibile
        }
      }
    },
    setDefaultAudioModel(state) {
      if (!state.audioModels || state.audioModels.length === 0) {
        return;
      }
      state.selectedModel = state.audioModels[0];
    },
    setShowAppleSubscriptionInfo(state, show) {
      state.showAppleSubscriptionInfo = show
    },
    setShowGoogleSubscriptionInfo(state, show) {
      state.showGoogleSubscriptionInfo = show
    },
    setPurchaseSuccessful(state, success) {
      state.purchaseSuccessful = success
    },
    setPurchaseError(state, error) {
      state.purchaseError = error
    },
    setReportMessageID(state, messageID) {
      state.reportMessageID = messageID
    },
    setShowProfileSettings(state, show) {
      state.showProfileSettings = show
    }, setShowLoginPopup(state, show) {
      state.showLoginPopup = show
    }, setSubscriptionFromAnotherPlatform(state, value) {
      state.subscriptionFromAnotherPlatform = value
    },
    setShowProfileDeletePopup(state, show) {
      state.showProfileDeletePopup = show
    },
    setShowUpgradeSubscriptionPopup(state, show) {
      if (show) {
        state.showSubscriptionUpdatedPopup = false
        state.showManageCancelledPlanPopup = false
        state.showReactivatePlanPopup = false
      }
      state.showUpgradeSubscriptionPopup = show
    },
    setShowManageCancelledPlanPopup(state, show) {
      if (show) {
        state.showProfileSettings = false
        state.showProfileDeletePopup = false
        state.showUpgradeSubscriptionPopup = false
        state.showReactivatePlanPopup = false
        state.showSubscriptionUpdatedPopup = false
        state.showCancelSubscriptionPopup = false
        state.showCancelSubscriptionOfferViolationPopup = false
        state.showSubscriptionCancelledPopup = false
      }
      state.showManageCancelledPlanPopup = show
    },
    setShowReactivatePlanPopup(state, show) {
      if (show) {
        state.showProfileSettings = false
        state.showProfileDeletePopup = false
        state.showUpgradeSubscriptionPopup = false
        state.showManageCancelledPlanPopup = false
        state.showSubscriptionUpdatedPopup = false
        state.showCancelSubscriptionPopup = false
        state.showCancelSubscriptionOfferViolationPopup = false
        state.showSubscriptionCancelledPopup = false
      }
      state.showReactivatePlanPopup = show
    },
    setShowSubscriptionUpdatedPopup(state, show) {
      if (show) {
        state.showUpgradeSubscriptionPopup = false
        state.showManageCancelledPlanPopup = false
        state.showReactivatePlanPopup = false
      }
      state.showSubscriptionUpdatedPopup = show
    },
    setSubscriptionCancellationContext(state, context) {
      if (!context) {
        state.subscriptionCancellationContext = null
        return
      }

      state.subscriptionCancellationContext = {
        reasonKey: typeof context.reasonKey === 'string' ? context.reasonKey : '',
        feedbackText: typeof context.feedbackText === 'string' ? context.feedbackText : ''
      }
    },
    setShowCancelSubscriptionPopup(state, show) {
      if (show) {
        state.showProfileSettings = false
        state.showProfileDeletePopup = false
        state.showUpgradeSubscriptionPopup = false
        state.showManageCancelledPlanPopup = false
        state.showReactivatePlanPopup = false
        state.showSubscriptionUpdatedPopup = false
        state.showCancelSubscriptionOfferViolationPopup = false
        state.showSubscriptionCancelledPopup = false
      }
      state.showCancelSubscriptionPopup = show
    },
    setShowCancelSubscriptionOfferViolationPopup(state, show) {
      if (show) {
        state.showProfileSettings = false
        state.showProfileDeletePopup = false
        state.showUpgradeSubscriptionPopup = false
        state.showManageCancelledPlanPopup = false
        state.showReactivatePlanPopup = false
        state.showSubscriptionUpdatedPopup = false
        state.showCancelSubscriptionPopup = false
        state.showSubscriptionCancelledPopup = false
      }
      state.showCancelSubscriptionOfferViolationPopup = show
    },
    setShowSubscriptionCancelledPopup(state, show) {
      if (show) {
        state.showProfileSettings = false
        state.showProfileDeletePopup = false
        state.showUpgradeSubscriptionPopup = false
        state.showManageCancelledPlanPopup = false
        state.showReactivatePlanPopup = false
        state.showSubscriptionUpdatedPopup = false
        state.showCancelSubscriptionPopup = false
        state.showCancelSubscriptionOfferViolationPopup = false
      }
      state.showSubscriptionCancelledPopup = show
    },
    setToolLimitPopup(state, { show, tool = null, hoursUntilReset = null }) {
      state.showToolLimitPopup = show
      state.toolLimitType = show ? tool : null
      state.toolLimitHoursUntilReset = show ? hoursUntilReset : null
    },
    setChatModels(state, models) {
      state.chatModels = models;
    },
    setImageModels(state, models) {
      state.imageModels = models;
    },
    setAudioModels(state, models) {
      state.audioModels = models;
    },
    addToolHistoryEntry(state, entry) {
      state.toolHistory.unshift(entry);
      if (state.toolHistory.length > 50) {
        state.toolHistory.pop();
      }
    },
    setMessageTextareaFullPage(state, isFullPage) {
      state.messageTextareaFullPage = isFullPage;
    },
    setIsMobile(state, isMobile) {
      state.isItMobile = isMobile;
    }
  },
  actions: {
    resetStore() {
      this.replaceState(initialState());
    },
    setUserDocAndUpdateModel({ commit, state }, userDoc) {
      commit('setUserDoc', userDoc);
      // Solo se i modelli sono già caricati e non c'è una chat specifica nella route
      if (state.models && state.models.length > 0 && (state.selectedModel === null || state.selectedModel.value === 'openai/gpt-5.4-nano' || state.selectedModel.value === 'openai/gpt-5.4-mini')) {
        commit('setDefaultChatModel');
      }
    }
  },
  modules: {
  }
})
